import { and, AnyStateConfig, assertEvent, DoneActorEvent, enqueueActions, setup } from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { ActivateFlowContext } from '../activate';
import { CheckEmailFlowContext } from '../checkEmail';
import { getUserGuards, isStateGuard } from '../helpers';
import { ACTIVATION_CODE_STORAGE_KEY } from '../ptvSisu/constants';
import { persistEmailToSession, readEmailFromSession } from '../ptvSisu/emailSession';
import { SignInFlowContext } from '../signin';

export const activateDeviceFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, getConfigByKey, queryClient, region } =
            rawParams as InitMachineParams;
        const isState = isStateGuard({ getConfigByKey, flowId: 'activateDevice', router, region });
        const userGuards = getUserGuards(queryClient);

        return setup({
            types: {
                context: {} as {
                    redirectUrl: string | null;
                    email: string;
                    success?: boolean;
                    url: string;
                    activationCode?: string;
                    device?: ActivateFlowContext['device'];
                    isCodeFromQueryAllowed: boolean;
                    hasConfirmedAccount?: boolean;
                },
                events: {} as
                    | DoneActorEvent<CheckEmailFlowContext, 'checkEmail'>
                    | DoneActorEvent<SignInFlowContext, 'signIn'>
                    | DoneActorEvent<ActivateFlowContext, 'activate'>
                    | { type: 'POP_STATE' }
                    | { type: 'ROUTE_CHANGE' }
                    | { type: 'CHECK_EMAIL' }
                    | { type: 'SIGN_IN' }
                    | { type: 'SIGN_UP' }
                    | { type: 'ALREADY_LOGGED_IN' }
                    // Dispatched by the /account/confirm page's "Continue" button to
                    // advance the parked `confirmAccount` state into `activate`.
                    | { type: 'CONFIRM_ACCOUNT' },
            },
            guards: {
                isCheckEmail: and(isState('checkEmail', 'inclusion')),
                isSignIn: and(isState('signIn', 'inclusion')),
                isSignUp: and(isState('signUp', 'inclusion')),
                isConfirmAccount: and(isState('confirmAccount', 'inclusion')),
                isActivate: and(isState('activate', 'inclusion')),
                isActivateAndNotLoggedIn: and(['isActivate', 'isAnonymous']),
                isActivateAndLoggedIn: and(['isActivate', 'isLoggedIn']),
                isActivateLoggedInUnconfirmed: and([
                    'isActivate',
                    'isLoggedIn',
                    ({ context }) => context?.hasConfirmedAccount !== true,
                ]),

                ...userGuards,
            },
            actions: {
                persistEmail: enqueueActions(({ enqueue }) => {
                    // Rehydrate context.email from this tab's sessionStorage so a fresh render of
                    // /account/sign-{in,up} (post-check-email or after a hard refresh) rebuilds the
                    // in-memory flow state. sessionStorage is per-tab, so a concurrent submission
                    // in another tab can't leak in here.
                    const emailFromSession = readEmailFromSession();
                    if (typeof emailFromSession === 'string' && emailFromSession.length > 0) {
                        enqueue.assign({ email: emailFromSession });
                    }
                }),
                goToActivateRoute: enqueueActions(({ context }) => {
                    // Replace the current page with /activate/device, preserving region
                    // E.g., /us/account/sign-in -> /us/activate/device
                    const currentPath = router.pathname;
                    const activatePath = currentPath.replace(
                        /\/account\/(sign-in|sign-up|check-email|confirm)/,
                        '/activate/device',
                    );

                    // Use activation code from context or query
                    const code = context.activationCode || router.query?.code;
                    const codeParam = typeof code === 'string' ? `?code=${code}` : '';

                    const finalUrl = activatePath + codeParam;
                    router.push(finalUrl);
                }),
                goToConfirmAccount: enqueueActions(({ context }) => {
                    // Navigate to /account/confirm, preserving region
                    // E.g., /us/activate/device -> /us/account/confirm
                    const currentPath = router.pathname;
                    const confirmPath = currentPath.replace(
                        /\/activate\/device/,
                        '/account/confirm',
                    );

                    // Preserve activation code if present
                    const code = context.activationCode || router.query?.code;
                    const codeParam = typeof code === 'string' ? `?code=${code}` : '';

                    const finalUrl = confirmPath + codeParam;
                    router.push(finalUrl);
                }),
                decideActivateRoute: enqueueActions(({ enqueue, check }) => {
                    // Anonymous users already leave `idle` through its `always` guards, which read
                    // the live pathname. Raising CHECK_EMAIL on top of that outlives the eventless
                    // transition and is consumed by whichever successor state the guards picked —
                    // the same failure mode the blueprint documents for ALREADY_LOGGED_IN.
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    }
                    if (typeof router.query?.code === 'string') {
                        enqueue.assign({ activationCode: router.query.code });
                        // Persist code to sessionStorage so it survives page navigations
                        try {
                            sessionStorage.setItem(ACTIVATION_CODE_STORAGE_KEY, router.query.code);
                        } catch (e) {
                            // Silently fail if sessionStorage is unavailable
                        }
                    }
                }),
                handleActivationCompletion: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.activate');
                    enqueue.assign({
                        success: true,
                        ...(typeof event.output?.activationCode === 'string'
                            ? { activationCode: event.output.activationCode }
                            : {}),
                        ...(event.output?.device ? { device: event.output.device } : {}),
                    });
                }),
                persistRedirectUrl: enqueueActions(({ enqueue }) => {
                    const redirectUrlFromQuery = router.query.redirectUrl;
                    enqueue.assign({
                        redirectUrl: Array.isArray(redirectUrlFromQuery)
                            ? redirectUrlFromQuery[0]
                            : redirectUrlFromQuery || null,
                    });
                }),
                checkUserStatus: enqueueActions(({ enqueue, check }) => {
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    }
                }),
                checkUserStatusEmail: enqueueActions(({ enqueue, check, context }) => {
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    } else if (!context.email) {
                        enqueue.raise({ type: 'CHECK_EMAIL' });
                    }
                }),
                handleCheckEmailCompletion: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.checkEmail');
                    enqueue.assign({ email: event.output.email });

                    // Stash the email in this tab's sessionStorage so the sign-in/sign-up
                    // client guard can rehydrate it. Per-tab scope means a submission in another tab
                    // can't overwrite it, and the value never enters a cookie / request header.
                    persistEmailToSession(event.output.email);

                    switch (event.output.destinationState) {
                        case 'signIn':
                            enqueue.raise({ type: 'SIGN_IN' });
                            break;
                        case 'signUp':
                            enqueue.raise({ type: 'SIGN_UP' });
                            break;
                    }
                }),
                markAccountAsConfirmed: enqueueActions(({ enqueue }) => {
                    enqueue.assign({ hasConfirmedAccount: true });
                }),
                checkAndAutoSubmitAfterConfirm: enqueueActions(({ context, enqueue }) => {
                    // When returning to activate from confirmAccount, try to auto-submit if conditions met
                    // We use sendTo to send an event to the activate child actor
                    if (context?.activationCode && context?.hasConfirmedAccount === true) {
                        enqueue.sendTo('activate', {
                            type: 'AUTO_SUBMIT',
                            params: { activationCode: context.activationCode },
                        });
                    }
                }),
                restoreActivationCode: enqueueActions(({ enqueue }) => {
                    // Restore activation code from sessionStorage after page reload
                    // This ensures the code persists through signin/signup redirects
                    try {
                        const storedCode = sessionStorage.getItem(ACTIVATION_CODE_STORAGE_KEY);
                        if (typeof storedCode === 'string' && storedCode.length > 0) {
                            enqueue.assign({ activationCode: storedCode });
                        }
                    } catch (e) {
                        // Silently fail if sessionStorage is unavailable
                    }
                }),
                clearActivationCode: enqueueActions(() => {
                    // Clear stored activation code when flow completes
                    try {
                        sessionStorage.removeItem(ACTIVATION_CODE_STORAGE_KEY);
                    } catch (e) {
                        // Silently fail if sessionStorage is unavailable
                    }
                }),
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                isCodeFromQueryAllowed: true,
                redirectUrl: null,
                email: '',
                url: '',
                ...input,
            }),
        });
    },
};
